import { useEffect, useMemo, useState } from "react";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PenLine, Send, Save } from "lucide-react";
import SignaturePad from "./SignaturePad";
import api from "@/lib/api";

/**
 * Patient-side PDF filler.
 * - Loads the original assigned PDF via /api/forms/{form_id}/pdf?auth=...
 * - Detects AcroForm text/checkbox fields and renders them as inline HTML inputs
 *   the patient can type into. If the PDF has no interactive fields we fall back
 *   to a free-text "notes" area + typed signature that we stamp onto the last page.
 * - On Save → uses pdf-lib to write the values back into the PDF and flatten it.
 * - Once "Save" completes, the "Send to clinician" button appears; that click
 *   uploads the annotated PDF to POST /api/form-responses/pdf.
 *
 * Props: assignment (form_assignment row with .form_id, .id) and onDone callback.
 */
export default function PdfFillEditor({ assignment, onDone }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // SEC-003 · Fetch a scope-limited media token before pointing pdf-lib
    // at the file. The full JWT never lands in the URL.
    (async () => {
      try {
        const { mediaSrc } = await import("@/lib/mediaSrc");
        const src = await mediaSrc("form", assignment.form_id);
        if (!cancelled) setPdfUrl(src);
      } catch {
        if (!cancelled) setPdfUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [assignment.form_id]);

  const [loading, setLoading]     = useState(true);
  const [originalPdf, setOriginalPdf] = useState(null);   // Uint8Array
  const [fields, setFields]       = useState([]);        // [{name,type,value,options}]
  const [notes, setNotes]         = useState("");
  const [signature, setSignature] = useState("");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(null);      // Uint8Array of filled+flattened PDF
  const [sending, setSending]     = useState(false);
  const [sent, setSent]           = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(pdfUrl);
        if (!res.ok) throw new Error("Cannot load PDF");
        const buf = new Uint8Array(await res.arrayBuffer());
        if (cancelled) return;
        setOriginalPdf(buf);
        // Detect any AcroForm fields to render as HTML inputs
        try {
          const pdf = await PDFDocument.load(buf, { ignoreEncryption: true });
          const form = pdf.getForm();
          const list = form.getFields().map((f) => {
            const name = f.getName();
            const t = f.constructor.name;   // e.g. PDFTextField, PDFCheckBox
            if (t === "PDFTextField")   return { name, type: "text",     value: f.getText() || "" };
            if (t === "PDFCheckBox")    return { name, type: "checkbox", value: f.isChecked() };
            if (t === "PDFDropdown")    return { name, type: "select",   value: (f.getSelected()[0] || ""), options: f.getOptions() };
            if (t === "PDFRadioGroup")  return { name, type: "radio",    value: f.getSelected() || "", options: f.getOptions() };
            return null;
          }).filter(Boolean);
          setFields(list);
        } catch (e) { /* PDF may be corrupted or encrypted — treat as flat */ }
      } catch (e) {
        toast.error("Couldn't open the PDF");
      } finally { setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  const updateField = (name, value) => {
    setFields((s) => s.map((f) => f.name === name ? { ...f, value } : f));
    setSaved(null); setSent(false);
  };

  const buildFilledPdf = async () => {
    const pdf = await PDFDocument.load(originalPdf, { ignoreEncryption: true });
    // 1) Fill interactive fields
    if (fields.length) {
      const form = pdf.getForm();
      fields.forEach((fld) => {
        try {
          if (fld.type === "text")     form.getTextField(fld.name).setText(String(fld.value || ""));
          if (fld.type === "checkbox") { const cb = form.getCheckBox(fld.name); fld.value ? cb.check() : cb.uncheck(); }
          if (fld.type === "select")   { if (fld.value) form.getDropdown(fld.name).select(fld.value); }
          if (fld.type === "radio")    { if (fld.value) form.getRadioGroup(fld.name).select(fld.value); }
        } catch { /* ignore malformed fields */ }
      });
      try { form.flatten(); } catch { /* older PDFs may reject flatten */ }
    }
    // 2) Stamp signature + notes onto the last page if patient provided any
    if (notes.trim() || signature) {
      const pages = pdf.getPages();
      const page  = pages[pages.length - 1];
      const { width, height } = page.getSize();
      const font  = await pdf.embedFont(StandardFonts.Helvetica);
      let y = 90;
      if (notes.trim()) {
        page.drawText("Patient notes:", { x: 40, y, size: 10, font, color: rgb(0.1, 0.1, 0.1) });
        y -= 14;
        const lines = wrapText(notes.trim(), 88);
        for (const ln of lines) { page.drawText(ln, { x: 40, y, size: 9, font, color: rgb(0.2, 0.2, 0.2) }); y -= 11; if (y < 40) break; }
      }
      if (signature) {
        try {
          const png = await pdf.embedPng(signature);
          const w = Math.min(160, png.width);
          const h = png.height * (w / png.width);
          page.drawText("Patient signature:", { x: width - 220, y: 90, size: 9, font, color: rgb(0.2, 0.2, 0.2) });
          page.drawImage(png, { x: width - 220, y: 30, width: w, height: h });
          page.drawText(new Date().toLocaleDateString("en-GB"), { x: width - 220, y: 24, size: 8, font, color: rgb(0.4, 0.4, 0.4) });
        } catch { /* signature not a PNG data URL */ }
      }
    }
    return await pdf.save();
  };

  const save = async () => {
    setSaving(true);
    try {
      const bytes = await buildFilledPdf();
      setSaved(bytes);
      setSent(false);
      toast.success("Saved locally — now tap Send to clinician");
    } catch (e) {
      toast.error("Couldn't save the PDF");
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!saved) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("assignment_id", assignment.id);
      fd.append("file", new Blob([saved], { type: "application/pdf" }),
                        `response-${assignment.id}.pdf`);
      await api.post("/form-responses/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      toast.success("Sent — your clinician has been notified.");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: "var(--fl-muted-2)" }}><Loader2 className="animate-spin inline mr-2" size={14}/> Loading PDF…</div>;

  return (
    <div className="space-y-4" data-testid="pdf-fill-editor">
      {/* Preview of the original PDF (patient can also open in new tab) */}
      <div className="rounded-md overflow-hidden border" style={{ borderColor: "var(--fl-border)" }}>
        <iframe title="original-pdf" src={pdfUrl} className="w-full" style={{ height: 420, background: "white" }} />
      </div>

      {/* Interactive fields, if any */}
      {fields.length > 0 && (
        <div>
          <div className="fl-label mb-2">Fill in the fields</div>
          <div className="space-y-2">
            {fields.map((f) => (
              <div key={f.name}>
                <div className="text-xs mb-1" style={{ color: "var(--fl-muted)" }}>{f.name}</div>
                {f.type === "text" && (
                  <input className="fl-input" value={f.value || ""}
                         onChange={(e) => updateField(f.name, e.target.value)}
                         data-testid={`pdf-field-${f.name}`} />
                )}
                {f.type === "checkbox" && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!f.value}
                           onChange={(e) => updateField(f.name, e.target.checked)}
                           data-testid={`pdf-field-${f.name}`} />
                    <span>Tick</span>
                  </label>
                )}
                {f.type === "select" && (
                  <select className="fl-input" value={f.value || ""}
                          onChange={(e) => updateField(f.name, e.target.value)}
                          data-testid={`pdf-field-${f.name}`}>
                    <option value="">— choose —</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
                {f.type === "radio" && (
                  <div className="flex flex-wrap gap-3">
                    {(f.options || []).map((o) => (
                      <label key={o} className="inline-flex items-center gap-1.5 text-sm">
                        <input type="radio" name={f.name} value={o} checked={f.value === o}
                               onChange={(e) => updateField(f.name, e.target.value)} />
                        {o}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Free-text notes always available */}
      <div>
        <div className="fl-label mb-1 flex items-center gap-2"><PenLine size={12}/> Additional notes</div>
        <textarea rows={4} className="fl-input" placeholder="Type any answers, symptoms, or clarifications you'd like your clinician to see…"
                  value={notes}
                  onChange={(e) => { setNotes(e.target.value); setSaved(null); setSent(false); }}
                  data-testid="pdf-notes" />
      </div>

      {/* Signature */}
      <div>
        <div className="fl-label mb-1">Signature (optional)</div>
        <SignaturePad value={signature}
                      onChange={(v) => { setSignature(v); setSaved(null); setSent(false); }}
                      testid="pdf-signature" />
      </div>

      {/* Action row */}
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={save} disabled={saving} className="fl-btn" data-testid="pdf-save">
          {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
          Save changes
        </button>
        {saved && !sent && (
          <button onClick={send} disabled={sending} className="fl-btn" data-testid="pdf-send"
                  style={{ background: "#22C55E", borderColor: "#22C55E" }}>
            {sending ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>}
            Send to clinician
          </button>
        )}
        {sent && (
          <span className="text-sm inline-flex items-center gap-1" style={{ color: "#22C55E" }}>
            <CheckCircle2 size={14}/> Sent
          </span>
        )}
      </div>
    </div>
  );
}

function wrapText(str, maxChars) {
  const words = str.split(/\s+/);
  const out = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars) { out.push(cur); cur = w; }
    else cur = (cur ? cur + " " : "") + w;
  }
  if (cur) out.push(cur);
  return out.slice(0, 20);   // cap
}
